import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { map, take, catchError } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfigurationService } from '../../configuration.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { ExhibitDetail } from '../../firestore-models/exhibit-detail';
import { ApiExhibitDetail } from '../../api-client-module';
import { ExhibitDetailAttribute } from '../../firestore-models/exhibit-detail-attribute';

import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-exhibit-details-dialog',
  templateUrl: './select-exhibit-details-dialog.component.html',
  styleUrls: ['./select-exhibit-details-dialog.component.css'],
  standalone: false,
})
export class SelectExhibitDetailsDialogComponent implements OnInit, OnDestroy {

  projectLanguages!: Array<ProjectLanguage>;

  private exhibitDetailsCollection!: AngularFirestoreCollection<ExhibitDetail>;
  exhibitDetailsObservable!: Observable<ExhibitDetail[]>;
  exhibitDetailsSubscribe!: Subscription;
  private exhibitDetailAttributesCollection!: AngularFirestoreCollection<ExhibitDetailAttribute>;
  exhibitDetailAttributesObservable!: Observable<ExhibitDetailAttribute[]>;
  exhibitDetailAttributesSubscribe!: Subscription;
  projectId: string;

  displayedColumns: string[] = ['position', 'select', 'exhibit_detail_id', 'attribute', 'name'];
  dataSource = new MatTableDataSource<ExhibitDetail>();
  selection = new SelectionModel<ExhibitDetail>(true, []);

  protected ATTR_SEARCH_WORD_PREFIX: string = 'exhibitDetailAttributes';
  exhibitDetailAttributes!: Array<ExhibitDetailAttribute>;

  searchFilter: any = {};

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  attrCollectionName = AppConst.FIRESTORE_EXHIBIT_DETAIL_ATTR;

  projectBaseLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<SelectExhibitDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiExhibitDetail,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
      this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectLanguages = this.configurationService.getProject().languages!;
    // 展示物取得
    this.exhibitDetailsCollection = this.angularFirestore.collection<ExhibitDetail>(
      AppConst.FIRESTORE_EXHIBIT_DETAILS, ref => ref.where('project_id', '==', this.projectId).orderBy('exhibit_detail_id', 'asc'));
    this.exhibitDetailsObservable = this.exhibitDetailsCollection.valueChanges({ idField: 'id' });
    this.exhibitDetailsSubscribe = this.exhibitDetailsObservable.subscribe(exhibitDetails => {
      this.dataSource.data = this.addFilterValue(exhibitDetails);
    });
    // 展示属性取得
    this.exhibitDetailAttributesCollection = this.angularFirestore.collection<ExhibitDetailAttribute>(
      this.attrCollectionName, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.exhibitDetailAttributesObservable = this.exhibitDetailAttributesCollection.valueChanges({ idField: 'id' });
    this.exhibitDetailAttributesSubscribe = this.exhibitDetailAttributesObservable.subscribe(exhibitDetailAttributes => {
      this.exhibitDetailAttributes = exhibitDetailAttributes;
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.exhibitDetailsSubscribe.unsubscribe();
    this.exhibitDetailAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: ExhibitDetail): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectExhibitDetails() {
    let exhibitDetails: Array<ApiExhibitDetail> = new Array<ApiExhibitDetail>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        const exhibitDetail: ApiExhibitDetail = {
          id: row.id,
          exhibit_detail_id: row.exhibit_detail_id,
          exhibit_detail_attribute_id: row.exhibit_detail_attribute_id,
          exhibit_detail_names: row.exhibit_detail_names,
          exhibit_detail_descs: row.exhibit_detail_descs,
          exhibit_detail_files: row.exhibit_detail_files,
          youtube_codes: row.youtube_codes,
          use_youtube: row.use_youtube,
          externallink_names: row.externallink_names,
          externallink_urls: row.externallink_urls,
          use_externallink: row.use_externallink,
          not_overlap_guide: row.not_overlap_guide,
          project_id: row.project_id,
          enabled: row.enabled,
        };
        exhibitDetails.push(exhibitDetail);
      }
    }
    this.dialogRef.close(exhibitDetails);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'exhibit_detail_id':
          return compare(a.exhibit_detail_id, b.exhibit_detail_id, isAsc);
        case 'attribute':
          const aAttr = a.exhibit_detail_attribute_id ? a.exhibit_detail_attribute_id : '';
          const bAttr = b.exhibit_detail_attribute_id ? b.exhibit_detail_attribute_id : '';
          return compare(aAttr, bAttr, isAsc);
        case 'name':
          const aName = a.exhibit_detail_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.exhibit_detail_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.exhibit_detail_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
      doc['filterValueAttr'] = this.ATTR_SEARCH_WORD_PREFIX + doc.exhibit_detail_attribute_id;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.ATTR_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
